import React from "react";
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { SORT_OPTIONS } from "./constants";

/**
 * One pill in the status segmented control. On a phone the row of pills is a
 * single horizontally-scrollable strip rather than a wrapping block, so the
 * control keeps its shape instead of becoming a two-row grid.
 */
function StatusTab({ tab, active, count, onClick }) {
  return (
    <Box
      role="tab"
      aria-selected={active}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      sx={{
        display: "flex", alignItems: "center", gap: 0.9,
        px: { xs: 1.25, sm: 1.6 }, py: { xs: 1, sm: 0.85 },
        minHeight: { xs: 38, sm: 0 }, flexShrink: 0,
        borderRadius: 2, cursor: "pointer", userSelect: "none",
        transition: "background-color .15s, box-shadow .15s, color .15s",
        color: active ? tab.color : "text.secondary",
        bgcolor: active ? "#fff" : "transparent",
        boxShadow: active ? "0 1px 2px rgba(15,23,42,.08), 0 0 0 1px rgba(15,23,42,.06)" : "none",
        "&:hover": { bgcolor: active ? "#fff" : "rgba(15,23,42,.04)" },
        "&:focus-visible": { outline: `2px solid ${tab.color}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: active ? tab.color : "#cbd5e1" }} />
      <Typography variant="body2" sx={{ fontWeight: active ? 700 : 500, whiteSpace: "nowrap", fontSize: 13 }}>
        {tab.label}
      </Typography>
      <Box
        sx={{
          minWidth: 20, px: 0.6, borderRadius: 1, textAlign: "center", fontSize: 11, fontWeight: 700,
          fontVariantNumeric: "tabular-nums", lineHeight: "17px",
          color: active ? tab.color : "#64748b",
          bgcolor: active ? `${tab.color}14` : "rgba(15,23,42,.06)",
        }}
      >
        {count ?? 0}
      </Box>
    </Box>
  );
}

/**
 * Status pills + search / brand / sort / view controls, in one card.
 *
 * Every control is laid out on a Grid so the row reflows into a single
 * column on a phone instead of a flex row that overflows the page.
 */
export default function CompilationsToolbar({
  statusTabs, statusFilter, onStatusChange, stats,
  searchInput, onSearchChange,
  brands, selectedBrand, onBrandChange,
  sort, onSortChange,
  view, onViewChange,
  shownCount,
}) {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        mb: 2.5,
        p: { xs: 1.25, sm: 1.75 },
        borderRadius: 3,
        borderColor: "#e6ebf1",
        bgcolor: "#fff",
        minWidth: 0,
      }}
    >
      <Box
        role="tablist"
        sx={{
          display: "flex", gap: 0.4, p: 0.4, mb: 1.5,
          borderRadius: 2.5, bgcolor: "#f1f5f9",
          flexWrap: { xs: "nowrap", md: "wrap" },
          overflowX: { xs: "auto", md: "visible" },
          width: { xs: "100%", md: "auto" },
          alignSelf: "flex-start",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {statusTabs.map((tab) => (
          <StatusTab
            key={tab.label}
            tab={tab}
            active={statusFilter === tab.value}
            count={stats[tab.statsKey]}
            onClick={() => onStatusChange(tab.value)}
          />
        ))}
      </Box>

      <Grid container spacing={1.25} alignItems="center">
        <Grid item xs={12} md={5} lg={4}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search title or WCA id…"
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange("")} aria-label="Clear search">
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            fullWidth
            options={brands}
            getOptionLabel={(b) => b.name}
            value={selectedBrand}
            onChange={(_, val) => onBrandChange(val)}
            isOptionEqualToValue={(o, v) => o.name === v?.name}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" } }}
            renderInput={(params) => <TextField {...params} label="Brand" placeholder="All brands…" />}
          />
        </Grid>

        <Grid item xs={7} sm={4} md={3} lg={2}>
          <TextField
            select
            size="small"
            fullWidth
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" } }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={5} sm={2} md="auto">
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={view}
            onChange={(_, v) => v && onViewChange(v)}
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: 2, px: 1.1, borderColor: "#e2e8f0",
                minHeight: { xs: 40, sm: 36 },
              },
            }}
          >
            <ToggleButton value="cards" aria-label="Card view">
              <Tooltip title="Card view — thumbnail-first">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="List view">
              <Tooltip title="List view — dense">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12} md sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
          >
            Showing <b>{shownCount}</b>
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}
